import React from 'react';
import Platform from "react-native-web/dist/exports/Platform";
import Pressable from "react-native-web/dist/exports/Pressable";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Text from "react-native-web/dist/exports/Text";
import View from "react-native-web/dist/exports/View";
import propTypes from './shared';
import { useFloating, arrow, offset, shift, flip, autoPlacement } from '../utils/floating-ui';
import { applyShadowToken, applyTextStyles, useThemeTokens } from '../ThemeProvider';
import { a11yProps, selectSystemProps, selectTokens, viewProps } from '../utils';
import TooltipButton from '../TooltipButton';
import useCopy from '../utils/useCopy';
import dictionary from './dictionary';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectTooltipStyles = _ref => {
  let {
    backgroundColor,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    borderRadius
  } = _ref;
  return {
    backgroundColor,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    borderRadius
  };
};
const selectTooltipShadowStyles = _ref2 => {
  let {
    shadow,
    borderRadius
  } = _ref2;
  return {
    borderRadius,
    ...applyShadowToken(shadow)
  };
};
const selectToolTipSizeStyles = _ref3 => {
  let {
    maxWidth,
    minWidth
  } = _ref3;
  return {
    maxWidth,
    minWidth
  };
};
const selectMobileTooltipPositionStyles = _ref4 => {
  let {
    top,
    left,
    position
  } = _ref4;
  return {
    top,
    left,
    position
  };
};
const selectMobileArrowStyles = (_ref5, _ref6) => {
  let {
    backgroundColor,
    arrowWidth,
    arrowBorderRadius,
    shadow
  } = _ref5;
  let {
    placement,
    x: arrowX,
    y: arrowY
  } = _ref6;
  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right'
  }[placement.split('-')[0]];
  return {
    backgroundColor,
    width: arrowWidth,
    height: arrowWidth,
    borderBottomRightRadius: arrowBorderRadius,
    // this corner will be the arrow tip after rotation
    transform: [{
      rotateZ: '45deg'
    }],
    ...applyShadowToken(shadow),
    left: arrowX != null && arrowX,
    top: arrowY != null && arrowY,
    right: '',
    bottom: '',
    [staticSide]: '-4px'
  };
};
const selectTextStyles = tokens => applyTextStyles(selectTokens('Typography', tokens));
const defaultControl = (pressableState, variant) => /*#__PURE__*/_jsx(TooltipButton, {
  pressableState: pressableState,
  variant: variant
});

/**
 * Tooltip provides a descriptive and detailed explanation or instructions. It can be used next to an input label
 * to help a user fill it in, or as a standalone component.
 *
 * By default the TooltipButton component will be used as a control for triggering the tooltip, but you may attach
 * a tooltip to any other component. A render function can be used to adjust the control's styling on state changes (hover, focus, etc.).
 *
 * ### Positioning
 * By default a Tooltip will be automatically positioned in a way that ensures it fits within the viewport.
 * You may suggest a position with a prop - it will be used, unless the tooltip would end up outside the viewport.
 *
 * ### Usage criteria
 * - You may use one when the information is useful only to a small percentage of users (ie. tech savvy people wouldn't need this info).
 * - Tooltips may also be useful when vertical space is an issue.
 */
const Tooltip = /*#__PURE__*/React.forwardRef((_ref7, ref) => {
  let {
    children,
    content,
    position = 'auto',
    copy = 'en',
    onPress = () => {},
    tokens,
    variant,
    inline = false,
    nativeID,
    ...rest
  } = _ref7;
  const [isOpen, setIsOpen] = React.useState(false);
  const arrowRef = React.useRef();
  const [tooltipDimensions, setTooltipDimensions] = React.useState(null);
  const positions = {
    auto: undefined,
    // Auto needs to specifically bet set to undefined
    above: 'top',
    right: 'right',
    below: 'bottom',
    left: 'left'
  };

  // "AutoPlacement" can't be used with "flip"
  const additionalMiddleware = position !== 'auto' ? autoPlacement : flip;
  const {
    x,
    y,
    reference,
    floating,
    strategy,
    placement,
    middlewareData: {
      arrow: arrowData = {}
    }
  } = useFloating({
    placement: positions[position],
    middleware: [shift(), additionalMiddleware(), offset(15), arrow({
      element: arrowRef
    })]
  });
  const getCopy = useCopy({
    dictionary,
    copy
  });
  const themeTokens = useThemeTokens('Tooltip', tokens, variant);
  const toggleIsOpen = () => {
    onPress();
    setIsOpen(!isOpen);
  };
  const close = () => setIsOpen(false);
  const getPressableState = _ref8 => {
    let {
      pressed,
      hovered,
      focused
    } = _ref8;
    return {
      pressed,
      hover: hovered,
      focus: focused
    };
  };
  const onTooltipLayout = _ref9 => {
    let {
      nativeEvent: {
        layout: {
          width,
          height
        }
      }
    } = _ref9;
    if (tooltipDimensions === null || tooltipDimensions.width !== width || tooltipDimensions.height !== height) {
      setTooltipDimensions({
        width: Platform.select({
          web: width + 0.3,
          // avoids often unnecessary line breaks due to subpixel rendering of fonts
          native: width
        }),
        height
      });
    }
  };
  React.useEffect(() => {
    if (!isOpen) {
      setTooltipDimensions(null);
    }
  }, [isOpen]);
  const control = children !== undefined ? children : defaultControl;
  const pressableStyles = control === defaultControl ? Platform.select({
    web: {
      outline: 'none'
    }
  }) : undefined;
  const pressableHitSlop = control === defaultControl ? {
    top: 10,
    bottom: 10,
    left: 10,
    right: 10
  } : undefined;
  return /*#__PURE__*/_jsxs(View, {
    style: [staticStyles.container, Platform.select({
      web: {
        display: inline ? 'inline-block' : 'flex'
      }
    })],
    ...selectProps(rest),
    ref: ref,
    children: [/*#__PURE__*/_jsx(Pressable, {
      onPress: toggleIsOpen,
      ref: reference,
      onBlur: close,
      style: pressableStyles,
      hitSlop: pressableHitSlop,
      accessibilityLabel: getCopy('a11yText'),
      accessibilityRole: "button",
      nativeID: nativeID,
      children: typeof control === 'function' ? pressableState => control(getPressableState(pressableState), variant) : control
    }), isOpen && /*#__PURE__*/_jsxs(View, {
      ref: floating,
      style: [selectTooltipShadowStyles(themeTokens), staticStyles.tooltip,
      // applied separately so that it doesn't cover the arrow
      selectToolTipSizeStyles, selectMobileTooltipPositionStyles({
        position: strategy,
        top: y ?? 0,
        left: x ?? 0
      })],
      onLayout: onTooltipLayout,
      accessibilityRole: "alert",
      children: [/*#__PURE__*/_jsx(View, {
        style: [staticStyles.arrow, selectMobileArrowStyles(themeTokens, {
          ...arrowData,
          placement
        })],
        ref: arrowRef
      }), /*#__PURE__*/_jsx(View, {
        style: selectTooltipStyles(themeTokens),
        children: /*#__PURE__*/_jsx(Text, {
          style: selectTextStyles(themeTokens),
          children: content
        })
      })]
    })]
  });
});
Tooltip.displayName = 'Tooltip';
Tooltip.propTypes = {
  ...selectedSystemPropTypes,
  ...propTypes
};
export default Tooltip;
const staticStyles = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
    position: 'absolute'
  },
  tooltip: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  arrow: {
    position: 'absolute'
  }
});